import { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaClipboardList, FaRocket, FaSyncAlt,
  FaLightbulb, FaChartLine, FaCogs, FaProjectDiagram,
  FaArrowRight,
} from 'react-icons/fa';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// ─── Client logos ────────────────────────────────────────────────
import fortuneIndia   from '../../assets/fortuneIndia.png';
import anytimefish    from '../../assets/anytimefish.png';
import sakuraLogo     from '../../assets/sakura_logo.png';
import srividhya      from '../../assets/srividhya.png';
import wintekDigital  from '../../assets/wintek Digital.png';
import logo42 from '../../assets/42.png'; import logo43 from '../../assets/43.png';
import logo44 from '../../assets/44.png'; import logo45 from '../../assets/45.png';
import logo46 from '../../assets/46.png'; import logo47 from '../../assets/47.png';
import logo48 from '../../assets/48.png'; import logo49 from '../../assets/49.png';
import logo50 from '../../assets/50.png'; import logo51 from '../../assets/51.png';
import logo52 from '../../assets/52.png'; import logo53 from '../../assets/53.png';
import logo54 from '../../assets/54.png'; import logo55 from '../../assets/55.png';
import logo56 from '../../assets/56.png'; import logo57 from '../../assets/57.png';
import logo58 from '../../assets/58.png'; import logo59 from '../../assets/59.png';

gsap.registerPlugin(ScrollTrigger);

// ─── Data ────────────────────────────────────────────────────────
const steps = [
  {
    num: '01', title: 'Request Services',
    desc: 'Submit unlimited digital requirements — campaigns, code, or creatives — and receive an initial scoped plan within 24 hours.',
    tag: 'Instant Brief',
    dir: 'left',
  },
  {
    num: '02', title: 'Receive Your Deliverable',
    desc: 'Production-ready campaigns and code ship within 1–3 business days. Reviewed, tested, and handoff-ready.',
    tag: '1–3 Day Delivery',
    dir: 'right',
  },
  {
    num: '03', title: 'Refine Until Perfect',
    desc: 'Unlimited revision cycles with your dedicated account manager until every detail meets your standard — no extra charge.',
    tag: 'Unlimited Rounds',
    dir: 'left',
  },
];

const features = [
  { title: 'Strategy Builder',    desc: 'Step-by-step roadmaps structured for enterprise SaaS and local brand scaling.',          icon: FaProjectDiagram },
  { title: 'Conversion Oriented', desc: 'Engineered to elevate campaign response and landing page conversion rates sustainably.',   icon: FaChartLine      },
  { title: 'Refining Strategies', desc: 'Continuous refinement cycle based on real-world platform telemetry and heatmaps.',        icon: FaCogs           },
  { title: 'Process Driven',      desc: 'Seamless execution through workflow automation, eliminating communication bottlenecks.',   icon: FaLightbulb      },
];

const clientLogos = [
  { src: fortuneIndia,  alt: 'Fortune India'   },
  { src: anytimefish,   alt: 'Anytime Fish'    },
  { src: sakuraLogo,    alt: 'Sakura'          },
  { src: srividhya,     alt: 'Srividhya'       },
  { src: wintekDigital, alt: 'Wintek Digital'  },
  { src: logo42, alt: 'Client 42' }, { src: logo43, alt: 'Client 43' },
  { src: logo44, alt: 'Client 44' }, { src: logo45, alt: 'Client 45' },
  { src: logo46, alt: 'Client 46' }, { src: logo47, alt: 'Client 47' },
  { src: logo48, alt: 'Client 48' }, { src: logo49, alt: 'Client 49' },
  { src: logo50, alt: 'Client 50' }, { src: logo51, alt: 'Client 51' },
  { src: logo52, alt: 'Client 52' }, { src: logo53, alt: 'Client 53' },
  { src: logo54, alt: 'Client 54' }, { src: logo55, alt: 'Client 55' },
  { src: logo56, alt: 'Client 56' }, { src: logo57, alt: 'Client 57' },
  { src: logo58, alt: 'Client 58' }, { src: logo59, alt: 'Client 59' },
];

/* ─── Animated SVG step icons ────────────────────────────────────── */

/* Icon 01 — lines type in sequentially, checkmark pops in, loops */
const StepIcon01 = () => (
  <svg viewBox="0 0 80 80" fill="none" className="w-full h-full">
    {/* Document outline — static */}
    <rect x="16" y="10" width="36" height="46" rx="4"
      stroke="#1C2E1E" strokeWidth="1.6" strokeLinecap="round" />

    {/* Line 1 — draws in first */}
    <motion.path d="M24 24 L44 24"
      stroke="#1C2E1E" strokeWidth="1.6" strokeLinecap="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: [0, 1, 1, 0] }}
      transition={{ duration: 3.6, times: [0, 0.22, 0.68, 1], repeat: Infinity, ease: 'easeInOut' }}
    />
    {/* Line 2 — delayed */}
    <motion.path d="M24 32 L44 32"
      stroke="#1C2E1E" strokeWidth="1.6" strokeLinecap="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: [0, 0, 1, 1, 0] }}
      transition={{ duration: 3.6, times: [0, 0.13, 0.38, 0.68, 1], repeat: Infinity, ease: 'easeInOut' }}
    />
    {/* Line 3 — last, shorter */}
    <motion.path d="M24 40 L36 40"
      stroke="#1C2E1E" strokeWidth="1.6" strokeLinecap="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: [0, 0, 0, 1, 1, 0] }}
      transition={{ duration: 3.6, times: [0, 0.08, 0.24, 0.50, 0.68, 1], repeat: Infinity, ease: 'easeInOut' }}
    />

    {/* Check circle — bounces in after lines, then fades out */}
    <motion.circle cx="56" cy="56" r="12" fill="#1C2E1E"
      animate={{ scale: [0, 0, 1.15, 1, 1, 0], opacity: [0, 0, 1, 1, 1, 0] }}
      transition={{ duration: 3.6, times: [0, 0.44, 0.56, 0.62, 0.72, 1], repeat: Infinity }}
      style={{ transformOrigin: '56px 56px' }}
    />
    {/* Checkmark path */}
    <motion.path d="M52 56 L56 60 L62 52"
      stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: [0, 0, 0, 1, 1, 0], opacity: [0, 0, 0, 1, 1, 0] }}
      transition={{ duration: 3.6, times: [0, 0.50, 0.57, 0.70, 0.78, 1], repeat: Infinity }}
    />
  </svg>
);

/* Icon 02 — arrow bounces down, shelf pulses on "landing", loops */
const StepIcon02 = () => (
  <svg viewBox="0 0 80 80" fill="none" className="w-full h-full">
    {/* Outer circle — static */}
    <circle cx="40" cy="38" r="22" stroke="#1C2E1E" strokeWidth="1.6" strokeLinecap="round" />

    {/* Arrow — bounces down */}
    <motion.g
      animate={{ y: [0, 5, 0] }}
      transition={{ duration: 1.4, repeat: Infinity, ease: [0.45, 0, 0.55, 1] }}
    >
      <path d="M40 26 L40 41" stroke="#1C2E1E" strokeWidth="2" strokeLinecap="round" />
      <path d="M34 35 L40 43 L46 35"
        stroke="#1C2E1E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </motion.g>

    {/* Landing shelf — expands + brightens on arrow impact */}
    <motion.path d="M30 50 L50 50"
      stroke="#1C2E1E" strokeWidth="1.6" strokeLinecap="round"
      animate={{ scaleX: [1, 1, 1.22, 1], opacity: [0.35, 0.35, 1, 0.35] }}
      transition={{ duration: 1.4, times: [0, 0.58, 0.74, 1], repeat: Infinity, ease: 'easeInOut' }}
      style={{ transformOrigin: '40px 50px' }}
    />

    {/* Side ticks — static */}
    <line x1="10" y1="34" x2="18" y2="34" stroke="#1C2E1E" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="62" y1="34" x2="70" y2="34" stroke="#1C2E1E" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

/* Icon 03 — refresh arc spins continuously, pencil bobs gently */
const StepIcon03 = () => (
  <svg viewBox="0 0 80 80" fill="none" className="w-full h-full">
    {/* Spinning refresh arc + arrow indicator */}
    <motion.g
      animate={{ rotate: 360 }}
      transition={{ duration: 2.6, repeat: Infinity, ease: 'linear' }}
      style={{ transformOrigin: '40px 40px' }}
    >
      <path d="M58 40 A18 18 0 1 1 22 40 A18 18 0 0 1 40 22"
        stroke="#1C2E1E" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M40 14 L40 22 L48 22"
        stroke="#1C2E1E" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </motion.g>

    {/* Pencil — bobs to suggest active refinement */}
    <motion.g
      animate={{ y: [0, -2.5, 0], rotate: [0, 3, 0, -3, 0] }}
      transition={{ duration: 2.0, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
      style={{ transformOrigin: '51px 33px' }}
    >
      <path d="M46 34 L54 26 L58 30 L50 38 Z"
        stroke="#1C2E1E" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="44" y1="36" x2="50" y2="28"
        stroke="#1C2E1E" strokeWidth="1.2" strokeLinecap="round" strokeOpacity="0.3" />
      <path d="M44 40 L42 44 L46 42 Z"
        stroke="#1C2E1E" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </motion.g>
  </svg>
);

const ICONS = [StepIcon01, StepIcon02, StepIcon03];

const fadeUp = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } };

// ─── Main Component ───────────────────────────────────────────────
const HowSection = () => {
  const lineRef = useRef(null);

  /* Grow the centre-line on scroll using GSAP ScrollTrigger (fires once, compositor-safe) */
  useEffect(() => {
    if (!lineRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(lineRef.current,
        { scaleY: 0 },
        {
          scaleY: 1,
          ease: 'none',
          transformOrigin: 'top',
          scrollTrigger: {
            trigger: lineRef.current,
            start: 'top 80%',
            end: 'bottom 30%',
            scrub: 0.6,
          },
        }
      );
    });
    return () => ctx.revert();
  }, []);

  return (
    <div className="w-full">

      {/* ══════════════════════════════════════════
          SECTION 1 — Our Process
      ══════════════════════════════════════════ */}
      <section className="relative bg-[#FAFBF9] border-t border-[#F1F3F1] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-28 sm:pt-40 pb-28 sm:pb-40 relative z-10">

          {/* ── Heading ── */}
          <motion.div
            initial="hidden" whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
            transition={{ duration: 0.65, ease: 'easeOut' }}
            className="mb-20 sm:mb-32"
          >
            <span className="text-[11px] uppercase tracking-[0.28em] text-[#5A635A] font-semibold block mb-6">
              ✦&nbsp;&nbsp;Workflow&nbsp;&nbsp;✦
            </span>
            <h2 className="leading-[1.0] tracking-tight mb-6">
              <span className="block text-4xl sm:text-5xl md:text-6xl font-normal text-black/25">Our</span>
              <span className="block text-5xl sm:text-6xl md:text-[80px] font-black text-black">
                <span className="bg-gradient-to-r from-[#FF7332] via-[#FF1E90] to-[#7000FF] bg-clip-text text-transparent inline pb-2">Process.</span>
              </span>
            </h2>
            <p className="text-base sm:text-lg text-[#5A635A] font-normal max-w-md leading-relaxed">
              A repeatable pipeline designed for speed, quality, and zero guesswork — from brief to delivery.
            </p>
          </motion.div>

          {/* ── Timeline ── */}
          <div className="relative">

            {/* Centre vertical line track + fill (desktop only) */}
            <div className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px" aria-hidden="true">
              <div className="absolute inset-0 bg-[#EAECE9]" />
              <div ref={lineRef} className="absolute inset-0 bg-[#1C2E1E]/40" />
            </div>

            {/* Steps */}
            <div className="flex flex-col gap-24 sm:gap-32 lg:gap-40">
              {steps.map((step, i) => {
                const StepIcon = ICONS[i];
                const isRight = step.dir === 'right';
                return (
                  <div key={i} className="relative">

                    {/* Timeline node dot (desktop) */}
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true, margin: '-60px' }}
                      transition={{ duration: 0.35, ease: 'easeOut', delay: 0.1 }}
                      className="hidden lg:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20
                        w-5 h-5 rounded-full bg-[#1C2E1E] items-center justify-center shadow-md"
                    >
                      <div className="w-2 h-2 rounded-full bg-white" />
                    </motion.div>

                    {/* Two-column layout */}
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-0 items-center">

                      {/* Content side */}
                      <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{ duration: 0.55, ease: 'easeOut' }}
                        className={`${isRight ? 'lg:order-last lg:pl-20' : 'lg:pr-20'}`}
                      >
                        <div className="flex items-center gap-4 mb-6">
                          <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#5A635A]/60">
                            Step {step.num}
                          </span>
                          <div className="flex-1 h-px bg-[#EAECE9]" />
                        </div>

                        <div
                          className="text-[clamp(5rem,14vw,9rem)] font-black leading-none tracking-tighter select-none mb-4"
                          style={{ color: 'rgba(0,0,0,0.04)' }}
                          aria-hidden="true"
                        >
                          {step.num}
                        </div>

                        <h3 className="text-3xl sm:text-4xl font-bold text-black tracking-tight leading-[1.1] mb-5">
                          {step.title}
                        </h3>
                        <p className="text-base text-[#5A635A] font-normal leading-relaxed max-w-sm">
                          {step.desc}
                        </p>

                        <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 border border-[#EAECE9] rounded-full bg-white">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#1C2E1E] flex-shrink-0" />
                          <span className="text-[11px] font-semibold text-black uppercase tracking-wider">
                            {step.tag}
                          </span>
                        </div>
                      </motion.div>

                      {/* SVG icon side */}
                      <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-60px' }}
                        transition={{ duration: 0.55, ease: 'easeOut', delay: 0.08 }}
                        className={`flex items-center justify-center ${isRight ? 'lg:pr-20 lg:justify-end' : 'lg:pl-20 lg:justify-start'}`}
                      >
                        <div className="relative w-[200px] h-[200px] sm:w-[240px] sm:h-[240px]">
                          <div className="absolute inset-0 rounded-full bg-white border border-[#EAECE9] shadow-sm" />
                          <div className="absolute inset-8">
                            <StepIcon />
                          </div>
                          {/* Static dashed orbit ring — no rotation animation */}
                          <div className="absolute inset-[-12px] rounded-full border border-dashed border-[#EAECE9]" />
                        </div>
                      </motion.div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Bottom note */}
            <motion.div
              initial="hidden" whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.55, ease: 'easeOut' }}
              className="mt-24 sm:mt-32 flex flex-col sm:flex-row items-start sm:items-center gap-6 border-t border-[#EAECE9] pt-10"
            >
              <div className="w-8 h-8 rounded-full bg-[#1C2E1E] flex items-center justify-center flex-shrink-0">
                <FaClipboardList size={13} className="text-white" />
              </div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#5A635A]">
                All steps are managed by a dedicated account manager · Zero handoff friction · 24-hr response SLA
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 2 — Methodology Bento
      ══════════════════════════════════════════ */}
      <section className="relative bg-white border-t border-[#F1F3F1] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-28 sm:py-40 relative z-10">

          {/* ── Heading row: left text + right stat trio ── */}
          <div className="lg:flex lg:items-end lg:justify-between lg:gap-16 mb-16 sm:mb-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#5A635A] font-semibold block mb-6">
                ✦&nbsp;&nbsp;Methodology&nbsp;&nbsp;✦
              </span>
              <h2 className="leading-[1.0] tracking-tight">
                <span className="block text-3xl sm:text-4xl md:text-5xl font-normal text-black/25">
                  Why our method
                </span>
                <span className="block text-4xl sm:text-5xl md:text-[64px] font-black text-black">
                  delivers.
                </span>
              </h2>
              <p className="text-base sm:text-lg text-[#5A635A] font-normal max-w-md mt-6 leading-relaxed">
                Every engagement runs on a four-pillar framework — strategy, conversion, refinement, and process — so nothing gets left to chance.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
              className="hidden lg:flex border border-[#EAECE9] rounded-2xl overflow-hidden divide-x divide-[#EAECE9] flex-shrink-0"
            >
              {[{ n: '500+', l: 'Projects' }, { n: '340%', l: 'Avg ROI' }, { n: '98%', l: 'Retention' }].map((s, i) => (
                <div key={i} className="px-7 py-5 text-center">
                  <div className="text-2xl font-bold text-black tabular-nums">{s.n}</div>
                  <div className="text-[10px] text-[#5A635A] uppercase tracking-wider font-semibold mt-0.5">{s.l}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* ── Bento grid ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">

            {/* 01 — Dark hero card, 2 cols */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="md:col-span-2 lg:col-span-2 bg-[#0a0a0a] rounded-[28px] p-9 sm:p-11 relative overflow-hidden flex flex-col justify-between group min-h-[300px]"
            >
              <div className="absolute right-8 top-4 select-none pointer-events-none"
                style={{ fontSize: 'clamp(80px,12vw,140px)', fontWeight: 900, color: 'rgba(255,255,255,0.04)', lineHeight: 1 }}>
                01
              </div>
              <div>
                <span className="inline-flex px-3 py-1.5 bg-white/[0.07] border border-white/10 rounded-full text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-8 w-fit block">
                  Planning
                </span>
                <div className="w-12 h-12 rounded-[14px] bg-white/[0.08] border border-white/[0.1] flex items-center justify-center mb-6">
                  <FaProjectDiagram className="text-white/60" size={18} />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 tracking-tight leading-snug max-w-sm">
                  Strategy Builder
                </h3>
                <p className="text-white/70 text-sm sm:text-base leading-relaxed max-w-xl">
                  Growth roadmaps engineered for enterprise SaaS scaling, local brand expansion, and every stage in between. Every strategy ships with measurable KPIs and a defined sprint calendar — not slide decks.
                </p>
              </div>
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-white/50 group-hover:text-white/80 transition-colors duration-200 mt-10 pt-6 border-t border-white/[0.06]">
                <span>Explore Strategy Services</span>
                <FaArrowRight size={9} />
              </div>
            </motion.div>

            {/* 02 — Light card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, ease: 'easeOut', delay: 0.07 }}
              className="bg-[#FAFBF9] border border-[#EAECE9] rounded-[28px] p-8 relative overflow-hidden flex flex-col justify-between group hover:border-[#1C2E1E] transition-colors duration-200 min-h-[260px]"
            >
              <div className="absolute right-5 top-3 text-[80px] font-black text-black/[0.04] leading-none select-none pointer-events-none">02</div>
              <div>
                <span className="inline-flex px-3 py-1.5 bg-[#F1F3F1] rounded-full text-[10px] font-semibold uppercase tracking-wider text-[#5A635A] mb-6 w-fit block">
                  Performance
                </span>
                <div className="w-11 h-11 rounded-[12px] bg-white border border-[#EAECE9] flex items-center justify-center mb-5 group-hover:bg-[#1C2E1E] group-hover:border-[#1C2E1E] transition-colors duration-200">
                  <FaChartLine className="text-[#1C2E1E] group-hover:text-white transition-colors duration-200" size={15} />
                </div>
                <h4 className="text-xl font-bold text-black mb-3 tracking-tight">Conversion Oriented</h4>
                <p className="text-[#5A635A] text-sm leading-relaxed">
                  Every deliverable is engineered for conversion — from ad creative structure to landing page hierarchy. We don't ship content; we ship outcomes.
                </p>
              </div>
            </motion.div>

            {/* 03 — Light card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, ease: 'easeOut', delay: 0.04 }}
              className="bg-[#FAFBF9] border border-[#EAECE9] rounded-[28px] p-8 relative overflow-hidden flex flex-col justify-between group hover:border-[#1C2E1E] transition-colors duration-200 min-h-[260px]"
            >
              <div className="absolute right-5 top-3 text-[80px] font-black text-black/[0.04] leading-none select-none pointer-events-none">03</div>
              <div>
                <span className="inline-flex px-3 py-1.5 bg-[#F1F3F1] rounded-full text-[10px] font-semibold uppercase tracking-wider text-[#5A635A] mb-6 w-fit block">
                  Analytics
                </span>
                <div className="w-11 h-11 rounded-[12px] bg-white border border-[#EAECE9] flex items-center justify-center mb-5 group-hover:bg-[#1C2E1E] group-hover:border-[#1C2E1E] transition-colors duration-200">
                  <FaCogs className="text-[#1C2E1E] group-hover:text-white transition-colors duration-200" size={15} />
                </div>
                <h4 className="text-xl font-bold text-black mb-3 tracking-tight">Refining Strategies</h4>
                <p className="text-[#5A635A] text-sm leading-relaxed">
                  Continuous iteration via real telemetry — heatmaps, A/B results, and ROAS attribution — applied in weekly refinement cycles, not quarterly reviews.
                </p>
              </div>
            </motion.div>

            {/* 04 — Wide horizontal white card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, ease: 'easeOut', delay: 0.08 }}
              className="md:col-span-2 lg:col-span-2 bg-white border border-[#EAECE9] rounded-[28px] p-8 sm:p-10 relative overflow-hidden flex flex-col sm:flex-row items-start gap-7 sm:gap-10 group hover:border-[#1C2E1E] transition-colors duration-200"
            >
              <div className="absolute right-8 bottom-4 text-[80px] font-black text-black/[0.03] leading-none select-none pointer-events-none">04</div>
              <div className="flex-shrink-0">
                <span className="inline-flex px-3 py-1.5 bg-[#F1F3F1] rounded-full text-[10px] font-semibold uppercase tracking-wider text-[#5A635A] mb-6 w-fit block">
                  Operations
                </span>
                <div className="w-12 h-12 rounded-[14px] bg-[#FAFBF9] border border-[#EAECE9] flex items-center justify-center group-hover:bg-[#1C2E1E] group-hover:border-[#1C2E1E] transition-colors duration-200">
                  <FaLightbulb className="text-[#1C2E1E] group-hover:text-white transition-colors duration-200" size={17} />
                </div>
              </div>
              <div>
                <h4 className="text-xl sm:text-2xl font-bold text-black mb-3 tracking-tight">Process Driven</h4>
                <p className="text-[#5A635A] text-sm sm:text-base leading-relaxed max-w-2xl">
                  Execution through documented workflows and zero handoff friction between marketing and engineering — from brief to delivery, every step tracked in a shared Slack channel with weekly video syncs.
                </p>
                <div className="flex flex-wrap gap-2 mt-5">
                  {['Documented Workflows', 'Automation Pipelines', 'Zero Friction Handoffs', 'Weekly Syncs'].map((t, i) => (
                    <span key={i} className="px-3 py-1.5 bg-[#FAFBF9] border border-[#EAECE9] rounded-lg text-[11px] font-semibold text-black">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 3 — Trusted Brands (Marquee)
      ══════════════════════════════════════════ */}
      <section className="relative bg-[#FAFBF9] border-t border-[#F1F3F1] overflow-hidden">

        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-28 sm:pt-40 pb-16 sm:pb-20 relative z-10">
          <motion.div
            initial="hidden" whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeUp}
            transition={{ duration: 0.65, ease: 'easeOut' }}
            className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8"
          >
            <div>
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#5A635A] font-semibold block mb-6">
                ✦&nbsp;&nbsp;Social Proof&nbsp;&nbsp;✦
              </span>
              <h2 className="leading-[1.05] tracking-tight">
                <span className="block text-3xl sm:text-4xl md:text-5xl font-normal text-black/25">
                  Trusted by
                </span>
                <span className="block text-4xl sm:text-5xl md:text-[64px] font-bold text-black">
                  <span className="bg-gradient-to-r from-[#FF7332] via-[#FF1E90] to-[#7000FF] bg-clip-text text-transparent inline pb-2">Forward-Thinking Brands</span>
                </span>
              </h2>
            </div>
            <p className="text-sm text-[#5A635A] font-normal max-w-xs leading-relaxed lg:text-right lg:pb-2">
              120+ businesses across healthcare, fintech, SaaS, logistics, and e-commerce trust us to deliver.
            </p>
          </motion.div>
        </div>

        {/* Marquee Row 1 — opacity fade edges only (no CSS mask on old browsers) */}
        <div className="border-t border-[#EAECE9] py-6 relative z-10 overflow-hidden"
          style={{ maskImage: 'linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)' }}>
          <div
            className="flex w-max gap-4"
            style={{ animation: 'ticker 32s linear infinite', willChange: 'transform', transform: 'translateZ(0)' }}
          >
            {[...clientLogos, ...clientLogos].map((client, i) => (
              <div key={i}
                className="flex-shrink-0 w-[140px] h-[72px] bg-white border border-[#EAECE9] rounded-2xl flex items-center justify-center px-5 group">
                <img
                  src={client.src} alt={client.alt}
                  className="max-h-8 w-auto max-w-[110px] object-contain opacity-40 group-hover:opacity-75 transition-opacity duration-200"
                  loading="lazy" decoding="async"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Marquee Row 2 */}
        <div className="border-t border-[#EAECE9] py-6 relative z-10 overflow-hidden"
          style={{ maskImage: 'linear-gradient(to right, transparent 0%, black 6%, black 94%, transparent 100%)' }}>
          <div
            className="flex w-max gap-4"
            style={{ animation: 'tickerRev 28s linear infinite', willChange: 'transform', transform: 'translateZ(0)' }}
          >
            {[...clientLogos.slice(11), ...clientLogos, ...clientLogos.slice(0, 11)].map((client, i) => (
              <div key={i}
                className="flex-shrink-0 w-[140px] h-[72px] bg-white border border-[#EAECE9] rounded-2xl flex items-center justify-center px-5 group">
                <img
                  src={client.src} alt={client.alt}
                  className="max-h-8 w-auto max-w-[110px] object-contain opacity-40 group-hover:opacity-75 transition-opacity duration-200"
                  loading="lazy" decoding="async"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Bottom stat strip */}
        <div className="border-t border-[#EAECE9] relative z-10">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-8 sm:py-10 pb-28 sm:pb-40">
            <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
              {[
                { n: '120+', l: 'Brands Served'         },
                { n: '12',   l: 'Industry Verticals'     },
                { n: '6+',   l: 'Countries Reached'      },
                { n: '98%',  l: 'Repeat Engagement Rate' },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xl sm:text-2xl font-bold text-black tabular-nums">{s.n}</span>
                  <span className="text-[11px] font-semibold text-[#5A635A] uppercase tracking-wider">{s.l}</span>
                  {i < 3 && <span className="hidden sm:block w-px h-4 bg-[#EAECE9] ml-3" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          SECTION 4 — Idea CTA (dark)
      ══════════════════════════════════════════ */}
      <section className="relative bg-[#0a0a0a] overflow-hidden border-t border-white/[0.05]">

        {/* Corner ambient glows — static, no filter */}
        <div className="absolute top-0 right-0 w-[600px] h-[500px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 80% 20%, rgba(112,0,255,0.08) 0%, transparent 60%)' }} />
        <div className="absolute bottom-0 left-0 w-[500px] h-[400px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 20% 80%, rgba(255,115,50,0.07) 0%, transparent 60%)' }} />

        <div className="relative z-10 max-w-[1200px] mx-auto px-6 sm:px-10 lg:px-16 flex flex-col justify-center min-h-[88vh] py-24 sm:py-32">

          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-center gap-4 mb-10 sm:mb-14"
          >
            <span className="text-[10px] font-mono tracking-[0.2em] text-white/20">05</span>
            <div className="h-px w-8 bg-white/10" />
            <span className="text-[10px] uppercase tracking-[0.28em] text-white/25 font-semibold">
              Let's Build Together
            </span>
          </motion.div>

          {/* Hero text */}
          <div className="mb-10 sm:mb-14">
            <motion.p
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
              className="text-[13px] sm:text-sm uppercase tracking-[0.22em] text-white/30 font-semibold mb-3 sm:mb-4"
            >
              Got an
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1], delay: 0.06 }}
              className="font-black tracking-tighter leading-none select-none"
              style={{
                fontSize: 'clamp(5.5rem, 22vw, 17rem)',
                background: 'linear-gradient(120deg,#FF7332 0%,#FF1E90 50%,#7000FF 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              IDEA?
            </motion.h2>
          </div>

          {/* Bottom row — copy LEFT · CTAs RIGHT */}
          <motion.div
            initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1], delay: 0.14 }}
            className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 lg:gap-20"
          >
            <div>
              <p className="font-extralight text-white/30 leading-[1.25] mb-1"
                style={{ fontSize: 'clamp(1.4rem, 3.5vw, 2.2rem)' }}>
                Stop thinking.
              </p>
              <p className="font-bold leading-[1.25]"
                style={{
                  fontSize: 'clamp(1.4rem, 3.5vw, 2.2rem)',
                  background: 'linear-gradient(90deg,#FF7332,#FF1E90)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                Start growing.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-shrink-0">
              <Link to="/contact"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full text-[13px] font-semibold text-white tracking-wide hover:opacity-90 transition-opacity duration-200 group"
                style={{ background: 'linear-gradient(120deg,#FF7332 0%,#FF1E90 55%,#7000FF 100%)' }}>
                Launch Project
                <FaArrowRight size={10} className="group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <Link to="/contact"
                className="inline-flex items-center gap-1.5 text-[13px] font-medium text-white/35 hover:text-white/70 transition-colors duration-200 group">
                Book a discovery call
                <FaArrowRight size={9} className="group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </div>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.25 }}
            className="border-t border-white/[0.06] mt-14 sm:mt-20 pt-6 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-10"
          >
            <p className="text-[11px] text-white/22 font-light tracking-wide">
              Trusted by <span className="text-white/40 font-semibold">120+ brands</span> across healthcare, fintech, SaaS &amp; e-commerce
            </p>
            <div className="hidden sm:block h-3 w-px bg-white/10" />
            <p className="text-[11px] text-white/22 font-light tracking-wide">
              Bangalore, India · 6+ Countries Served
            </p>
          </motion.div>

        </div>
      </section>

    </div>
  );
};

export default HowSection;
